import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  Stack,
  useTheme,
  alpha,
} from '@mui/material';
import {
  CreditCard,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
  Warning as WarningIcon,
  CheckCircle,
  Schedule,
} from '@mui/icons-material';
import httpClient from '../services/httpClient';
import { useNavigate } from 'react-router-dom';

const MySubscription = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelImmediate, setCancelImmediate] = useState(false);

  const fetchSubscriptionStatus = useCallback(async () => {
    try {
      setLoading(true);
      const response = await httpClient.get('/v1/subscription/checkout/status');
      setSubscription(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch subscription status:', err);
      setError('Failed to load subscription status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscriptionStatus();
  }, [fetchSubscriptionStatus]);

  const handleCancel = async (immediate = false) => {
    try {
      setActionLoading(true);
      setError(null);
      
      await httpClient.post('/v1/subscription/checkout/cancel', { immediate });
      
      setSuccess(immediate 
        ? 'Your subscription has been cancelled immediately.' 
        : 'Your subscription will be cancelled at the end of the billing period.');
      
      setCancelDialogOpen(false);
      fetchSubscriptionStatus();
    } catch (err) {
      console.error('Failed to cancel subscription:', err);
      setError(err.response?.data?.error || 'Failed to cancel subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    try {
      setActionLoading(true);
      setError(null);
      
      await httpClient.post('/v1/subscription/checkout/reactivate');
      
      setSuccess('Your subscription has been reactivated!');
      fetchSubscriptionStatus();
    } catch (err) {
      console.error('Failed to reactivate subscription:', err);
      setError(err.response?.data?.error || 'Failed to reactivate subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenPortal = async () => {
    try {
      setActionLoading(true);
      setError(null);
      
      const response = await httpClient.post('/v1/subscription/checkout/portal', {
        returnUrl: window.location.href,
      });
      
      // Redirect to Stripe Customer Portal
      window.location.href = response.data.url;
    } catch (err) {
      console.error('Failed to open customer portal:', err);
      setError(err.response?.data?.error || 'Failed to open billing portal');
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'TRIAL':
        return 'warning';
      case 'CANCELLED':
      case 'EXPIRED':
        return 'error';
      case 'SUSPENDED':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ACTIVE':
        return <CheckCircle color="success" />;
      case 'TRIAL':
        return <Schedule color="warning" />;
      case 'CANCELLED':
      case 'EXPIRED':
      case 'SUSPENDED':
        return <CancelIcon color="error" />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <CreditCard sx={{ mr: 2, fontSize: 40, color: theme.palette.primary.main }} />
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            My Subscription
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your EyeSense subscription
          </Typography>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* No Subscription */}
      {!subscription?.hasSubscription && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Active Subscription
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Subscribe to get full access to EyeSense features
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/subscription/plans')}
            >
              View Plans
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Active Subscription Details */}
      {subscription?.hasSubscription && (
        <>
          {/* Pending Cancellation Warning */}
          {subscription.cancelAtPeriodEnd && (
            <Alert 
              severity="warning" 
              sx={{ mb: 3 }}
              icon={<WarningIcon />}
              action={
                <Button 
                  color="inherit" 
                  size="small"
                  onClick={handleReactivate}
                  disabled={actionLoading}
                >
                  Reactivate
                </Button>
              }
            >
              <Typography variant="body2">
                <strong>Cancellation Pending:</strong> Your subscription will end on {formatDate(subscription.endDate)}.
                You'll continue to have access until then.
              </Typography>
            </Alert>
          )}

          {/* Subscription Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {getStatusIcon(subscription.status)}
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                      {subscription.planType} Plan
                    </Typography>
                    <Chip 
                      label={subscription.cancelAtPeriodEnd ? 'Cancelling' : subscription.status} 
                      color={subscription.cancelAtPeriodEnd ? 'warning' : getStatusColor(subscription.status)} 
                      size="small" 
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                    ${subscription.price || '15.00'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    per month
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">Status</Typography>
                  <Typography fontWeight="bold">
                    {subscription.cancelAtPeriodEnd ? 'Active (Cancelling)' : subscription.status}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">Started</Typography>
                  <Typography>{formatDate(subscription.startDate)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">
                    {subscription.cancelAtPeriodEnd ? 'Ends On' : 'Next Billing Date'}
                  </Typography>
                  <Typography 
                    sx={{ 
                      color: subscription.cancelAtPeriodEnd ? theme.palette.warning.main : 'inherit',
                      fontWeight: subscription.cancelAtPeriodEnd ? 'bold' : 'normal'
                    }}
                  >
                    {formatDate(subscription.endDate)}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Actions Card */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Manage Subscription
              </Typography>
              
              <Stack spacing={2} sx={{ mt: 2 }}>
                {/* Stripe Customer Portal */}
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<OpenInNewIcon />}
                  onClick={handleOpenPortal}
                  disabled={actionLoading}
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Open Billing Portal (Update Payment Method, View Invoices)
                </Button>

                {/* Reactivate Button */}
                {subscription.cancelAtPeriodEnd && (
                  <Button
                    fullWidth
                    variant="contained"
                    color="success"
                    startIcon={<RefreshIcon />}
                    onClick={handleReactivate}
                    disabled={actionLoading}
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    {actionLoading ? <CircularProgress size={20} /> : 'Reactivate Subscription'}
                  </Button>
                )}

                {/* Cancel Button */}
                {!subscription.cancelAtPeriodEnd && subscription.status !== 'CANCELLED' && (
                  <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    startIcon={<CancelIcon />}
                    onClick={() => {
                      setCancelImmediate(false);
                      setCancelDialogOpen(true);
                    }}
                    disabled={actionLoading}
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    Cancel Subscription
                  </Button>
                )}
              </Stack>
            </CardContent>
          </Card>
        </>
      )}

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Cancel Subscription
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Are you sure you want to cancel your subscription?
          </DialogContentText>
          
          <Box sx={{ 
            p: 2, 
            bgcolor: alpha(theme.palette.info.main, 0.1), 
            borderRadius: 1,
            mb: 2 
          }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Recommended: Cancel at period end
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your subscription will remain active until {formatDate(subscription?.endDate)}. 
              You can reactivate anytime before then.
            </Typography>
          </Box>

          <Box sx={{ 
            p: 2, 
            bgcolor: alpha(theme.palette.error.main, 0.1), 
            borderRadius: 1 
          }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Immediate cancellation
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your access will end immediately. No refunds for the remaining period.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => setCancelDialogOpen(false)}
            disabled={actionLoading}
          >
            Keep Subscription
          </Button>
          <Button 
            onClick={() => handleCancel(true)}
            color="error"
            disabled={actionLoading}
          >
            Cancel Immediately
          </Button>
          <Button 
            onClick={() => handleCancel(false)}
            variant="contained"
            color="warning"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Cancel at Period End'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MySubscription;

